import {
  CSSProperties,
  ElementType,
  MouseEventHandler,
  RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react"

import { getPrefixedClassNames, styles } from "./styles"

import { HoverCellRendererProps } from "./types"
import { cx } from "@emotion/css"

const ENTER_TIMEOUT = 200
let enterTimerHandle: number
function startEnteringTimer(cb: () => void) {
  window.clearTimeout(enterTimerHandle)
  enterTimerHandle = window.setTimeout(() => {
    cb()
  }, ENTER_TIMEOUT)
}
function clearEnteringTimer() {
  window.clearTimeout(enterTimerHandle)
}

const LEAVE_TIMEOUT = 100
let leaveTimerHandle: number

function startLeavingTimer(cb: () => void) {
  leaveTimerHandle = window.setTimeout(() => {
    cb()
  }, LEAVE_TIMEOUT)
}
function clearLeavingTimer() {
  window.clearTimeout(leaveTimerHandle)
}

type HoverCellProps = {
  open: boolean
  rowIndex: number
  rowId: string
  renderer: ElementType<HoverCellRendererProps>
  // box: RectReadOnly
  rowRef: RefObject<HTMLDivElement | null>
}

export function HoverCell(props: HoverCellProps) {
  const { open, renderer, rowIndex, rowId, rowRef } = props
  const [visible, setVisible] = useState<boolean>(() => false)
  const mounted = useRef(false)
  const entered = useRef(false)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
      clearEnteringTimer()
      clearLeavingTimer()
    }
  }, [])

  const handleOutsideRequest = () => {
    if (!mounted.current) return
    if (entered.current) return
    setVisible(open)
  }
  const _handleOutsideRequest = useRef(handleOutsideRequest)
  _handleOutsideRequest.current = handleOutsideRequest

  const handleInsideRequest = () => {
    if (!mounted.current) return
    setVisible(entered.current)
  }
  const _handleInsideRequest = useRef(handleInsideRequest)
  _handleInsideRequest.current = handleInsideRequest

  useEffect(() => {
    if (open) {
      startEnteringTimer(() => {
        _handleOutsideRequest.current()
      })
    } else {
      startLeavingTimer(() => {
        _handleOutsideRequest.current()
      })
    }
  }, [open])

  const handleMouseEnter: MouseEventHandler<HTMLDivElement> = (evt) => {
    evt.stopPropagation()
    entered.current = true
    clearLeavingTimer()
  }

  const handleMouseLeave: MouseEventHandler<HTMLDivElement> = (evt) => {
    evt.stopPropagation()
    entered.current = false
    startLeavingTimer(() => {
      _handleInsideRequest.current()
    })
  }

  // TODO: add click outside handler, maybe?

  const close = useCallback(() => {
    entered.current = false
    // no timer here, just close it
    _handleInsideRequest.current()
  }, [])

  if (!renderer) return null
  if (!visible) return null
  const box = rowRef.current?.getBoundingClientRect()
  if (!box) return null

  const vars = {
    "--top": `${box.top}px`,
    "--left": `${box.left}px`,
    "--right": `${box.right}px`,
    "--width": `${box.width}px`,
    "--height": `${box.height}px`,
  } as CSSProperties

  const Content = renderer

  return (
    <div
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={cx(styles.hoverCell, getPrefixedClassNames("hover-cell"))}
      style={vars}
    >
      <Content rowIndex={rowIndex} rowId={rowId} close={close} />
    </div>
  )
}
